import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import './ConcurrentUsersIndicator.css';

/**
 * Component to show which users are currently viewing/editing the same project
 * Uses Supabase Realtime presence for real-time user tracking
 * 
 * @param {Object} props - Component props
 * @param {string} props.projectId - The ID of the current project
 * @returns {JSX.Element} ConcurrentUsersIndicator component
 */
const ConcurrentUsersIndicator = ({ projectId }) => {
  const [activeUsers, setActiveUsers] = useState([]);
  const { user } = useAuth();
  const [channel, setChannel] = useState(null);

  useEffect(() => {
    if (!projectId || !user) return;

    // Create a presence channel specific to this project
    const presenceChannel = supabase.channel(`project:${projectId}`, {
      config: {
        presence: {
          key: user.id,
        },
      },
    });

    // Set up presence handlers
    presenceChannel
      .on('presence', { event: 'sync' }, () => {
        // Get current state of all users in the channel
        const state = presenceChannel.presenceState();
        
        // Convert to array of users
        const usersArray = Object.entries(state).map(([userId, userStates]) => {
          // userStates is an array, typically with just one entry per user
          const userData = userStates[0];
          return {
            id: userId,
            email: userData.email,
            name: userData.name || userData.email?.split('@')[0] || 'Unknown user',
            online_at: userData.online_at
          };
        });
        
        setActiveUsers(usersArray);
      })
      .on('presence', { event: 'join' }, ({ key, newPresence }) => {
        console.log('User joined:', key, newPresence);
      })
      .on('presence', { event: 'leave' }, ({ key, leftPresence }) => {
        console.log('User left:', key, leftPresence);
      })
      .subscribe(async (status) => {
        if (status !== 'SUBSCRIBED') {
          console.error('Failed to subscribe to presence channel:', status);
          return;
        }
        
        // Track this user's presence
        await presenceChannel.track({
          email: user.email || user.user_metadata?.email,
          name: user.user_metadata?.name,
          online_at: new Date().toISOString()
        });
        
        setChannel(presenceChannel);
      });

    // Clean up subscription when component unmounts
    return () => {
      if (presenceChannel) {
        presenceChannel.unsubscribe();
      }
    };
  }, [projectId, user]);

  // No need to show anything if we're the only user
  if (activeUsers.length <= 1) {
    return null;
  }

  // Filter out current user to show only other users
  const otherUsers = activeUsers.filter(activeUser => activeUser.id !== user.id);
  
  if (otherUsers.length === 0) {
    return null;
  }

  return (
    <div className="concurrent-users-indicator">
      <div className="concurrent-users-count">
        {otherUsers.length} other {otherUsers.length === 1 ? 'user' : 'users'} viewing this project
      </div>
      <div className="concurrent-users-list">
        {otherUsers.map(activeUser => (
          <div key={activeUser.id} className="concurrent-user">
            {activeUser.name}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConcurrentUsersIndicator; 